import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { 
  ShoppingCart, Plus, Minus, Trash2, CreditCard, Lock, Loader2, 
  CheckCircle, Clock, AlertCircle, MapPin, X, Mail, Receipt, Percent, Navigation, RefreshCw, DollarSign, Edit2, Settings, Send, Copy, List, Maximize, Minimize, Banknote, FileText, Users, Calendar, ShieldCheck
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import DistanceCalculator from './DistanceCalculator';
import RecurringClientSelector from './RecurringClientSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Styling for individual Stripe elements to match dark theme
const ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#64748b',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
    complete: {
      color: '#22c55e',
      iconColor: '#22c55e',
    },
  },
};

// Stripe Submit Button Component for POS using proper Elements API with separated fields
const POSStripeSubmitButton = ({ onApprove, onError, disabled, total, customerEmail, customerName, metadata, postalCode }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);

  const handleClick = async () => {
    if (!stripe || !elements) {
      onError(new Error("Payment system not ready. Please refresh and try again."));
      return;
    }
    
    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      onError(new Error("Card details not ready. Please try again."));
      return;
    }
    
    // Validate amount before proceeding
    if (!total || total <= 0) {
      onError(new Error("Invalid payment amount. Please refresh and try again."));
      return;
    }
    
    // Round to 2 decimal places to avoid floating point issues
    const chargeAmount = Math.round(total * 100) / 100;
    
    setPaying(true);
    try {
      // Log the exact amount being sent to Stripe
      console.log('💳 POS Payment - Sending to Stripe:', {
        originalTotal: total,
        chargeAmount: chargeAmount,
        chargeAmountFixed: chargeAmount.toFixed(2),
        customerEmail: customerEmail,
        customerName: customerName,
        metadata: metadata
      });
      
      // Create PaymentIntent on backend with validated amount
      const intentResponse = await axios.post(`${API}/payments/create-intent`, {
        amount: chargeAmount,
        description: 'Point of Sale Transaction',
        customer_email: customerEmail,
        customer_name: customerName,
        metadata: metadata,
      });
      
      console.log('💳 PaymentIntent created:', intentResponse.data);

      if (!intentResponse.data.success) {
        throw new Error(intentResponse.data.error || 'Failed to create payment');
      }

      const { client_secret, payment_intent_id } = intentResponse.data;

      // Confirm the payment with Stripe using the card number element
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: customerName || undefined,
            email: customerEmail || undefined,
            address: {
              postal_code: postalCode || undefined,
            },
          },
        },
      });

      if (error) {
        console.error("Payment error:", error);
        onError(new Error(error.message || "Payment failed"));
      } else if (paymentIntent.status === 'succeeded') {
        await onApprove({ 
          paymentIntentId: paymentIntent.id,
          status: paymentIntent.status 
        });
      }
    } catch (error) {
      console.error("Payment error:", error);
      let errorMessage = "Payment failed";
      if (error.message) {
        errorMessage = error.message;
      }
      onError(new Error(errorMessage));
    } finally {
      setPaying(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      disabled={disabled || paying || total <= 0 || !stripe}
      className="w-full h-14 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-lg rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {paying ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          Processing...
        </>
      ) : (
        <>
          <Lock className="h-5 w-5" />
          Charge ${total.toFixed(2)}
        </>
      )}
    </Button>
  );
};

const POSSystem = ({ open, onClose, classTypes = [] }) => {
  // Fullscreen kiosk mode state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [exitTapCount, setExitTapCount] = useState(0);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const exitTapTimeout = useRef(null);
  const posContainerRef = useRef(null);
  
  // Handle fullscreen change events and disable right-click in kiosk mode
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (!document.fullscreenElement) {
        setShowExitConfirm(false);
      }
    };
    
    // Disable right-click context menu in fullscreen mode
    const handleContextMenu = (e) => {
      if (document.fullscreenElement) {
        e.preventDefault();
        return false;
      }
    };
    
    // Disable Escape key from exiting fullscreen
    const handleKeyDown = (e) => {
      if (document.fullscreenElement && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);
  
  // Enter fullscreen mode - use the POS container element
  const enterFullscreen = useCallback(async () => {
    try {
      // Find the dialog content element or use document
      const elem = posContainerRef.current || document.documentElement;
      
      if (elem.requestFullscreen) {
        await elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen();
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
      // Try with document.body as fallback
      try {
        if (document.body.requestFullscreen) {
          await document.body.requestFullscreen();
        }
      } catch (e) {
        toast.error('Unable to enter fullscreen mode. Your browser may be blocking it.');
      }
    }
  }, []);
  
  // Exit fullscreen mode
  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    } catch (error) {
      console.error('Exit fullscreen error:', error);
    }
    setShowExitConfirm(false);
  }, []);
  
  // Handle triple-tap in corner to show exit confirmation
  const handleExitTap = useCallback(() => {
    if (!isFullscreen) return;
    
    setExitTapCount(prev => {
      const newCount = prev + 1;
      
      if (exitTapTimeout.current) {
        clearTimeout(exitTapTimeout.current);
      }
      
      if (newCount >= 3) {
        setShowExitConfirm(true);
        return 0;
      }
      
      exitTapTimeout.current = setTimeout(() => {
        setExitTapCount(0);
      }, 1000);
      
      return newCount;
    });
  }, [isFullscreen]);
  
  // Cart state
  const [cartItems, setCartItems] = useState([]);
  const [manualFees, setManualFees] = useState({
    afterHoursQty: 0,
    rushFeeQty: 0,
    mileage: 0
  });
  
  // Customer info for WooCommerce
  const [customerName, setCustomerName] = useState({ firstName: '', lastName: '' });
  const [customerEmail, setCustomerEmail] = useState('');
  const [selectedRecurringClient, setSelectedRecurringClient] = useState(null);
  
  // Discount state - type can be 'dollar' or 'percent'
  const [discount, setDiscount] = useState({ type: 'dollar', value: 0, reason: '' });
  
  // Custom item state
  const [customItems, setCustomItems] = useState([]);
  const [showAddCustomItem, setShowAddCustomItem] = useState(false);
  const [newCustomItem, setNewCustomItem] = useState({ name: '', price: '' });
  
  // Distance calculator state
  const [showDistanceCalc, setShowDistanceCalc] = useState(false);
  
  // Payment state
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [cardFieldStatus, setCardFieldStatus] = useState({
    cardNumber: { complete: false, error: null },
    cardExpiry: { complete: false, error: null },
    cardCvc: { complete: false, error: null },
  });
  
  // Receipt state
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [lastOrderId, setLastOrderId] = useState(null);
  
  // Fee rates from WooCommerce
  const [feeRates, setFeeRates] = useState({
    afterHoursPerUnit: 0,
    rushPerUnit: 0,
    mileagePerMile: 0
  });
  
  // Class types with WooCommerce prices
  const [classTypesWithPrices, setClassTypesWithPrices] = useState([]);
  const [rentalProducts, setRentalProducts] = useState([]);
  const [disposableProducts, setDisposableProducts] = useState([]);
  const [addonProducts, setAddonProducts] = useState([]);
  const [ecardProducts, setEcardProducts] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [pricesCacheInfo, setPricesCacheInfo] = useState({ lastUpdated: null, updatedBy: null });
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  
  // Reschedule fees state
  const [rescheduleFees, setRescheduleFees] = useState([]);
  const [rescheduleFeesInCart, setRescheduleFeesInCart] = useState([]);
  const [showReschedFeeSettings, setShowReschedFeeSettings] = useState(false);
  const [editingFees, setEditingFees] = useState([]);
  const [savingFees, setSavingFees] = useState(false);
  
  // Pending orders state
  const [pendingOrders, setPendingOrders] = useState([]);
  const [showPendingOrders, setShowPendingOrders] = useState(false);
  const [loadedFromPendingOrder, setLoadedFromPendingOrder] = useState(null); // Track if cart was loaded from pending

  useEffect(() => {
    if (open) {
      fetchPaymentConfig();
      fetchCachedPrices();  // Use cached prices instead of direct WooCommerce calls
      fetchRescheduleFees();
      fetchPendingOrders();
      // Reset state
      setCartItems([]);
      setManualFees({ afterHoursQty: 0, rushFeeQty: 0, mileage: 0 });
      setCustomerName({ firstName: '', lastName: '' });
      setDiscount({ type: 'dollar', value: 0, reason: '' });
      setCustomItems([]);
      setRescheduleFeesInCart([]);
      setShowAddCustomItem(false);
      setNewCustomItem({ name: '', price: '' });
      setShowReceiptDialog(false);
      setReceiptEmail('');
      setCustomerEmail('');
      setPaymentReady(false);
      setSelectedRecurringClient(null);
    }
  }, [open]);
  
  const fetchPaymentConfig = async () => {
    try {
      // Fetch Stripe config for the publishable key
      const response = await axios.get(`${API}/stripe/config`);
      setPaymentConfig(response.data);
      if (response.data?.publishable_key) {
        setPaymentReady(true);
        // Initialize Stripe
        const stripe = loadStripe(response.data.publishable_key);
        setStripePromise(stripe);
      }
    } catch (error) {
      console.error('Failed to fetch payment config:', error);
      toast.error('Failed to initialize payment system');
    }
  };
  
  // Fetch cached prices from MongoDB
  const fetchCachedPrices = async () => {
    setLoadingPrices(true);
    try {
      const response = await axios.get(`${API}/pos/cached-prices`);
      const { prices, last_updated, updated_by } = response.data;
      
      // Update cache info
      setPricesCacheInfo({ lastUpdated: last_updated, updatedBy: updated_by });
      
      // Extract fee rates
      const rushPrice = prices['RUSH']?.price || 0;
      const afterHoursPrice = prices['AFTERHOURS']?.price || 0;
      const mileagePrice = prices['MILES']?.price || 0;
      
      setFeeRates({
        rushPerUnit: rushPrice,
        afterHoursPerUnit: afterHoursPrice,
        mileagePerMile: mileagePrice
      });
      
      console.log('Fee rates from cache:', { rush: rushPrice, afterHours: afterHoursPrice, mileage: mileagePrice });
      
      // Get active class types with SKUs and match with cached prices
      const activeTypes = classTypes.filter(ct => ct.is_active && ct.sku);
      const typesWithPrices = activeTypes.map(ct => {
        const cachedPrice = prices[ct.sku];
        return {
          ...ct,
          wooPrice: cachedPrice?.price || 0,  // Default to 0 if no cached price
          wooName: cachedPrice?.name || ct.name
        };
      });
      
      console.log('Class types with prices:', typesWithPrices.map(ct => ({ id: ct.id, name: ct.name, sku: ct.sku, price: ct.wooPrice })));
      
      setClassTypesWithPrices(typesWithPrices);
      
      // Extract rental products
      const rentals = Object.values(prices).filter(p => p.type === 'rental' && p.price > 0);
      setRentalProducts(rentals.map(r => ({
        id: r.sku,
        name: r.name,
        sku: r.sku,
        wooPrice: r.price,
        type: 'rental',
        description: r.description || ''
      })));
      console.log('Rental products from cache:', rentals.length);
      
      // Extract disposable supplies products
      const disposables = Object.values(prices).filter(p => p.type === 'disposable' && p.price > 0);
      setDisposableProducts(disposables.map(d => ({
        id: d.sku,
        name: d.name,
        sku: d.sku,
        wooPrice: d.price,
        type: 'disposable',
        description: d.description || ''
      })));
      console.log('Disposable products from cache:', disposables.length);
      
      // Extract add-on products
      const addons = Object.values(prices).filter(p => p.type === 'addon' && p.price > 0);
      setAddonProducts(addons.map(a => ({
        id: a.sku,
        name: a.name,
        sku: a.sku,
        wooPrice: a.price,
        type: 'addon',
        description: a.description || ''
      })));
      console.log('Add-on products from cache:', addons.length);
      
      // Fetch instructor eCard products
      try {
        const ecardsResponse = await axios.get(`${API}/instructor-ecards/products`);
        const ecards = ecardsResponse.data || [];
        setEcardProducts(ecards.map(e => ({
          id: `ECARD-${e.product_number}`,
          name: e.name,
          sku: `ECARD-${e.product_number}`,
          wooPrice: e.price || 0,
          type: 'ecard',
          product_number: e.product_number,
          description: e.description || ''
        })));
        console.log('eCard products:', ecards.length);
      } catch (ecardError) {
        console.log('No instructor eCard pricing available');
        setEcardProducts([]);
      }
      
    } catch (error) {
      console.error('Failed to fetch cached prices:', error);
      toast.error('Failed to load prices');
      setClassTypesWithPrices(classTypes.filter(ct => ct.is_active));
      setRentalProducts([]);
      setDisposableProducts([]);
      setAddonProducts([]);
      setEcardProducts([]);
    } finally {
      setLoadingPrices(false);
    }
  };
  
  // Manual refresh prices from WooCommerce
  const refreshPricesManually = async () => {
    setRefreshingPrices(true);
    try {
      const response = await axios.post(`${API}/pos/refresh-prices`);
      toast.success(response.data.message || 'Prices refreshed successfully');
      // Refetch cached prices
      await fetchCachedPrices();
    } catch (error) {
      console.error('Failed to refresh prices:', error);
      toast.error(error.response?.data?.detail || 'Failed to refresh prices from WooCommerce');
    } finally {
      setRefreshingPrices(false);
    }
  };
  
  // Fetch reschedule fees
  const fetchRescheduleFees = async () => {
    try {
      const response = await axios.get(`${API}/pos/reschedule-fees`);
      setRescheduleFees(response.data.fees || []);
    } catch (error) {
      console.error('Failed to fetch reschedule fees:', error);
    }
  };
  
  // Add reschedule fee to cart
  const addRescheduleFee = (fee) => {
    const existingFee = rescheduleFeesInCart.find(f => f.id === fee.id);
    if (existingFee) {
      setRescheduleFeesInCart(rescheduleFeesInCart.map(f =>
        f.id === fee.id ? { ...f, quantity: f.quantity + 1 } : f
      ));
    } else {
      setRescheduleFeesInCart([...rescheduleFeesInCart, { ...fee, quantity: 1, isRescheduleFee: true }]);
    }
  };
  
  // Update reschedule fee quantity
  const updateRescheduleFeeQuantity = (feeId, delta) => {
    setRescheduleFeesInCart(rescheduleFeesInCart.map(fee => {
      if (fee.id === feeId) {
        const newQty = fee.quantity + delta;
        return newQty > 0 ? { ...fee, quantity: newQty } : fee;
      }
      return fee;
    }).filter(fee => fee.quantity > 0));
  };
  
  // Remove reschedule fee
  const removeRescheduleFee = (feeId) => {
    setRescheduleFeesInCart(rescheduleFeesInCart.filter(f => f.id !== feeId));
  };
  
  // Get reschedule fees total
  const getRescheduleFeesTotal = () => {
    return rescheduleFeesInCart.reduce((sum, fee) => sum + (fee.price * fee.quantity), 0);
  };
  
  // Open reschedule fee settings
  const openReschedFeeSettings = () => {
    setEditingFees([...rescheduleFees]);
    setShowReschedFeeSettings(true);
  };
  
  // Update editing fee price
  const updateEditingFeePrice = (feeId, newPrice) => {
    setEditingFees(editingFees.map(fee => 
      fee.id === feeId ? { ...fee, price: parseFloat(newPrice) || 0 } : fee
    ));
  };
  
  // Save reschedule fees
  const saveRescheduleFees = async () => {
    setSavingFees(true);
    try {
      const password = prompt('Enter admin password to save changes:');
      if (!password) {
        setSavingFees(false);
        return;
      }
      
      const response = await axios.put(`${API}/pos/reschedule-fees`, {
        password,
        fees: editingFees
      });
      
      if (response.data.success) {
        setRescheduleFees(response.data.fees);
        setShowReschedFeeSettings(false);
        toast.success('Reschedule fees updated');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save fees');
    } finally {
      setSavingFees(false);
    }
  };
  
  // Fetch pending orders
  const fetchPendingOrders = async () => {
    try {
      const response = await axios.get(`${API}/pos/pending-orders`);
      setPendingOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch pending orders:', error);
    }
  };
  
  // Convert cart to Quote and send to customer
  const [convertingToQuote, setConvertingToQuote] = useState(false);
  const [createdQuoteId, setCreatedQuoteId] = useState(null);
  
  const convertToQuoteAndSend = async () => {
    // Validate required fields
    if (getTotal() <= 0) {
      toast.error('Add items to create a quote');
      return;
    }
    
    if (!customerName.firstName?.trim() && !customerName.lastName?.trim()) {
      toast.error('Please enter customer name');
      return;
    }
    
    if (!customerEmail?.trim()) {
      toast.error('Please enter customer email');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setConvertingToQuote(true);
    try {
      // Build line items from cart
      const quoteItems = [];
      
      // Add cart items (classes, rentals, disposables, addons, ecards)
      cartItems.forEach(item => {
        quoteItems.push({
          description: item.name,
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.price) || 0,
          sku: item.sku || ''
        });
      });
      
      // Add custom items
      customItems.forEach(item => {
        quoteItems.push({
          description: item.name,
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.price) || 0,
          sku: 'CUSTOM'
        });
      });
      
      // Add reschedule fees
      rescheduleFeesInCart.forEach(fee => {
        quoteItems.push({
          description: fee.name,
          quantity: parseInt(fee.quantity) || 1,
          unit_price: parseFloat(fee.price) || 0,
          sku: 'RESCHED-FEE'
        });
      });
      
      // Add additional fees
      const afterHoursQty = parseInt(manualFees.afterHoursQty) || 0;
      const rushFeeQty = parseInt(manualFees.rushFeeQty) || 0;
      const mileageQty = parseInt(manualFees.mileage) || 0;
      
      if (afterHoursQty > 0 && feeRates.afterHoursPerUnit > 0) {
        quoteItems.push({
          description: 'After Hours Fee',
          quantity: afterHoursQty,
          unit_price: parseFloat(feeRates.afterHoursPerUnit) || 0,
          sku: 'AFTERHOURS'
        });
      }
      
      if (rushFeeQty > 0 && feeRates.rushPerUnit > 0) {
        quoteItems.push({
          description: 'Last Minute Fee',
          quantity: rushFeeQty,
          unit_price: parseFloat(feeRates.rushPerUnit) || 0,
          sku: 'RUSH'
        });
      }
      
      if (mileageQty > 0 && feeRates.mileagePerMile > 0) {
        quoteItems.push({
          description: `Travel Miles (${mileageQty} mi)`,
          quantity: mileageQty,
          unit_price: parseFloat(feeRates.mileagePerMile) || 0,
          sku: 'MILES'
        });
      }
      
      // Build discount object for quote
      let quoteDiscount = null;
      if (discount.value > 0) {
        quoteDiscount = {
          type: discount.type,
          value: discount.value,
          reason: discount.reason || 'POS Discount'
        };
      }
      
      // Create the quote via invoice API
      const createResponse = await axios.post(`${API}/invoices`, {
        invoice_type: 'quote',
        customer: {
          first_name: customerName.firstName || '',
          last_name: customerName.lastName || '',
          email: customerEmail,
          phone: '',
          company: '',
          address: ''
        },
        items: quoteItems,
        discount: quoteDiscount,
        allow_partial_payments: false,
        status: 'sent',  // Mark as sent immediately
        customer_notes: 'Created from Point of Sale',
        internal_notes: loadedFromPendingOrder ? `Converted from pending order #${loadedFromPendingOrder}` : 'Created from POS'
      });
      
      if (createResponse.data.invoice_id) {
        const quoteId = createResponse.data.invoice_id;
        setCreatedQuoteId(quoteId);
        
        // Send the quote email
        await axios.post(`${API}/invoices/${quoteId}/send`);
        
        toast.success(`Quote ${quoteId} created and sent to ${customerEmail}!`);
        
        // If loaded from pending order, delete it
        if (loadedFromPendingOrder) {
          try {
            await axios.delete(`${API}/pos/pending-orders/${loadedFromPendingOrder}`);
            fetchPendingOrders();
          } catch (e) {
            // Ignore delete errors
          }
        }
        
        // Clear the cart for a new order
        startAnotherOrder();
      }
    } catch (error) {
      console.error('Quote creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create quote');
    } finally {
      setConvertingToQuote(false);
    }
  };
  
  // Save order to pending orders (without showing payment link UI) and clear cart
  // If loaded from a pending order, updates that order instead of creating a new one
  const [savingToPending, setSavingToPending] = useState(false);
  const saveToPendingOrders = async () => {
    if (getTotal() <= 0) {
      toast.error('Add items to save order');
      return;
    }
    
    setSavingToPending(true);
    try {
      const orderData = {
        customer_name: customerName,
        customer_email: customerEmail,
        items: cartItems.map(item => ({
          sku: item.sku,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          type: item.type,
          days: item.days
        })),
        custom_items: customItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        reschedule_fees: rescheduleFeesInCart.map(fee => ({
          id: fee.id,
          name: fee.name,
          quantity: fee.quantity,
          price: fee.price
        })),
        fees: {
          after_hours_qty: manualFees.afterHoursQty,
          rush_qty: manualFees.rushFeeQty,
          mileage: manualFees.mileage
        },
        discount: {
          type: discount.type,
          value: discount.value || 0,
          amount: getDiscountAmount(),
          reason: discount.reason || ''
        },
        total: getTotal()
      };
      
      let response;
      if (loadedFromPendingOrder) {
        // Update existing order instead of creating new one
        response = await axios.put(`${API}/pos/pending-orders/${loadedFromPendingOrder}`, orderData);
        if (response.data.success) {
          toast.success(`Order #${loadedFromPendingOrder} updated`);
        }
      } else {
        // Create new pending order
        response = await axios.post(`${API}/pos/pending-orders`, orderData);
        if (response.data.success) {
          toast.success(`Order #${response.data.order_id} saved to pending orders`);
        }
      }
      
      fetchPendingOrders();
      startAnotherOrder(); // Clear cart for new order
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save order');
    } finally {
      setSavingToPending(false);
    }
  };
  
  // Start another order - clear POS for new order
  const startAnotherOrder = () => {
    setCartItems([]);
    setCustomItems([]);
    setRescheduleFeesInCart([]);
    setManualFees({ afterHoursQty: 0, rushFeeQty: 0, mileage: 0 });
    setCustomerName({ firstName: '', lastName: '' });
    setCustomerEmail('');
    setDiscount({ type: 'dollar', value: 0, reason: '' });
    setLoadedFromPendingOrder(null);
    setShowAddCustomItem(false);
    setNewCustomItem({ name: '', price: '' });
    setSelectedRecurringClient(null);
    toast.success('Ready for new order');
  };
  
  // Load pending order into cart
  const loadPendingOrder = (order) => {
    // Clear current cart
    setCartItems([]);
    setCustomItems([]);
    setRescheduleFeesInCart([]);
    
    // Track that we loaded from a pending order
    setLoadedFromPendingOrder(order.order_id);
    
    // Load items - need to match with classTypesWithPrices
    const loadedItems = [];
    for (const item of order.items || []) {
      const matchedClass = classTypesWithPrices.find(ct => ct.sku === item.sku);
      if (matchedClass) {
        loadedItems.push({
          id: matchedClass.id,
          name: item.name,
          sku: item.sku,
          price: item.price,
          quantity: item.quantity
        });
      }
    }
    setCartItems(loadedItems);
    
    // Load custom items
    if (order.custom_items?.length > 0) {
      setCustomItems(order.custom_items.map((item, idx) => ({
        id: `custom-loaded-${idx}`,
        name: item.name,
        price: item.price,
        quantity: item.quantity
      })));
    }
    
    // Load reschedule fees
    if (order.reschedule_fees?.length > 0) {
      setRescheduleFeesInCart(order.reschedule_fees.map(fee => ({
        id: fee.id,
        name: fee.name,
        price: fee.price,
        quantity: fee.quantity,
        isRescheduleFee: true
      })));
    }
    
    // Load customer info
    setCustomerName(order.customer_name || { firstName: '', lastName: '' });
    setCustomerEmail(order.customer_email || '');
    
    // Load fees
    const fees = order.fees || {};
    setManualFees({
      afterHoursQty: fees.after_hours_qty || 0,
      rushFeeQty: fees.rush_qty || 0,
      mileage: fees.mileage || 0
    });
    
    // Load discount
    const disc = order.discount || {};
    setDiscount({
      type: disc.type || 'dollar',
      value: disc.value || 0,
      reason: disc.reason || ''
    });
    
    setShowPendingOrders(false);
    toast.success(`Loaded order ${order.order_id}`);
  };
  
  // Delete pending order
  const deletePendingOrder = async (e, orderId) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!window.confirm('Delete this pending order?')) return;
    
    try {
      await axios.delete(`${API}/pos/pending-orders/${orderId}`);
      toast.success('Order deleted');
      fetchPendingOrders();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };
  
  // Add item to cart - uses WooCommerce price (wooPrice) from fetched data
  const addToCart = (classType) => {
    // Use wooPrice from WooCommerce, not local DB price
    const itemPrice = classType.wooPrice || parseFloat(classType.price) || 0;
    const itemType = classType.type || 'class';
    
    const existingItem = cartItems.find(item => item.id === classType.id);
    if (existingItem) {
      setCartItems(cartItems.map(item => 
        item.id === classType.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCartItems([...cartItems, {
        id: classType.id,
        name: classType.name,
        sku: classType.sku,
        price: itemPrice,
        quantity: 1,
        type: itemType,
        days: itemType === 'rental' ? 1 : undefined // Only rentals have days
      }]);
    }
  };
  
  // Update days for rental items
  const updateRentalDays = (itemId, days) => {
    const newDays = Math.max(1, parseInt(days) || 1);
    setCartItems(cartItems.map(item => 
      item.id === itemId ? { ...item, days: newDays } : item
    ));
  };
  
  // Update quantity
  const updateQuantity = (itemId, delta) => {
    setCartItems(cartItems.map(item => {
      if (item.id === itemId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };
  
  // Remove item
  const removeItem = (itemId) => {
    setCartItems(cartItems.filter(item => item.id !== itemId));
  };
  
  // Custom item functions
  const addCustomItem = () => {
    if (!newCustomItem.name.trim() || !newCustomItem.price) {
      toast.error('Please enter item name and price');
      return;
    }
    const price = parseFloat(newCustomItem.price);
    if (isNaN(price) || price <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    
    const customItem = {
      id: `custom_${Date.now()}`,
      name: newCustomItem.name.trim(),
      price: price,
      quantity: 1,
      isCustom: true
    };
    setCustomItems([...customItems, customItem]);
    setNewCustomItem({ name: '', price: '' });
    setShowAddCustomItem(false);
    toast.success('Custom item added');
  };
  
  const removeCustomItem = (itemId) => {
    setCustomItems(customItems.filter(item => item.id !== itemId));
  };
  
  const updateCustomItemQuantity = (itemId, delta) => {
    setCustomItems(customItems.map(item => {
      if (item.id === itemId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };
  
  // Get custom items total
  const getCustomItemsTotal = () => {
    return customItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };
  
  // Calculate totals
  const getSubtotal = () => {
    return cartItems.reduce((sum, item) => {
      const days = item.type === 'rental' ? (item.days || 1) : 1;
      return sum + (item.price * item.quantity * days);
    }, 0);
  };
  
  const getTotalQuantity = () => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  };
  
  const getFeesTotal = () => {
    let fees = 0;
    if (manualFees.afterHoursQty > 0) fees += feeRates.afterHoursPerUnit * manualFees.afterHoursQty;
    if (manualFees.rushFeeQty > 0) fees += feeRates.rushPerUnit * manualFees.rushFeeQty;
    return fees;
  };
  
  const getTravelMilesTotal = () => {
    if (manualFees.mileage > 0) return feeRates.mileagePerMile * manualFees.mileage;
    return 0;
  };
  
  // Calculate actual discount amount based on type (dollar or percent)
  const getDiscountAmount = () => {
    const subtotalWithFees = getSubtotal() + getFeesTotal() + getTravelMilesTotal() + getCustomItemsTotal() + getRescheduleFeesTotal();
    if (!discount.value || discount.value <= 0) return 0;
    
    if (discount.type === 'percent') {
      // Calculate percentage of subtotal+fees+custom, cap at 100%
      const percent = Math.min(discount.value, 100);
      return (subtotalWithFees * percent) / 100;
    } else {
      // Dollar amount, cap at subtotal+fees+custom
      return Math.min(discount.value, subtotalWithFees);
    }
  };
  
  const getTotal = () => {
    const subtotalWithFees = getSubtotal() + getFeesTotal() + getTravelMilesTotal() + getCustomItemsTotal() + getRescheduleFeesTotal();
    const discountAmount = getDiscountAmount();
    return Math.max(0, subtotalWithFees - discountAmount);
  };
  
  // Create Stripe order
  const createPOSStripeOrder = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/pos/create-order`, {
        items: cartItems.map(item => ({
          sku: item.sku,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          type: item.type,
          days: item.days
        })),
        custom_items: customItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        reschedule_fees: rescheduleFeesInCart.map(fee => ({
          id: fee.id,
          name: fee.name,
          quantity: fee.quantity,
          price: fee.price
        })),
        fees: {
          after_hours_qty: manualFees.afterHoursQty,
          rush_qty: manualFees.rushFeeQty,
          mileage: manualFees.mileage
        },
        discount: {
          type: discount.type,
          value: discount.value || 0,
          amount: getDiscountAmount(),
          reason: discount.reason || ''
        },
        total_quantity: getTotalQuantity()
      });
      
      return response.data.stripe_order_id;
    } catch (error) {
      console.error('Error creating POS order:', error);
      toast.error('Failed to create order');
      throw error;
    }
  }, [cartItems, customItems, manualFees, discount]);
  
  // Memoized Stripe hosted fields styles to prevent re-render issues
  const stripeFieldStyles = useMemo(() => ({
    'input': {
      'color': '#ffffff',
      'font-size': '14px',
      'background-color': '#334155',
      'border-radius': '6px',
      'padding': '12px'
    },
    ':focus': {
      'color': '#ffffff'
    },
    '.invalid': {
      'color': '#ef4444'
    }
  }), []);
  
  // Handle successful payment
  const handlePaymentSuccess = async (data) => {
    setPaymentProcessing(true);
    try {
      // Payment is already captured via Stripe PaymentIntent - no need to call capture endpoint
      // The PaymentIntent was confirmed in POSStripeSubmitButton
      
      // Create WooCommerce order with the Stripe payment_intent_id
      const wooRes = await axios.post(`${API}/pos/complete-order`, {
        stripe_payment_intent_id: data.paymentIntentId,
        customer_first_name: customerName.firstName || 'POS',
        customer_last_name: customerName.lastName || 'Customer',
        customer_email: customerEmail || null,  // Optional email for WooCommerce customer lookup
        items: cartItems.map(item => ({
          sku: item.sku,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          type: item.type,
          days: item.days
        })),
        custom_items: customItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        reschedule_fees: rescheduleFeesInCart.map(fee => ({
          id: fee.id,
          name: fee.name,
          quantity: fee.quantity,
          price: fee.price
        })),
        fees: {
          after_hours: manualFees.afterHours,
          rush: manualFees.rushFee,
          mileage: manualFees.mileage
        },
        discount: {
          type: discount.type,
          value: discount.value || 0,
          amount: getDiscountAmount(),
          reason: discount.reason || ''
        },
        total_quantity: getTotalQuantity(),
        total: getTotal()
      });
      
      setLastOrderId(wooRes.data.woo_order_id);
      toast.success('Payment successful!');
      
      // Show receipt dialog and auto-fill email if provided
      setReceiptEmail(customerEmail || '');
      setShowReceiptDialog(true);
      
    } catch (error) {
      console.error('Payment capture error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setPaymentProcessing(false);
    }
  };
  
  // Stripe callbacks
  const onStripeApprove = async (data) => {
    await handlePaymentSuccess(data);
  };
  
  const onStripeError = (error) => {
    console.error('Stripe error:', error);
    toast.error(error.message || 'Payment failed. Please try again.');
    setPaymentProcessing(false);
  };
  
  // Cash payment handler
  const [processingCash, setProcessingCash] = useState(false);
  const handleCashPayment = async () => {
    if (getTotal() <= 0) {
      toast.error('Add items to process payment');
      return;
    }
    
    if (!window.confirm(`Confirm cash payment of $${getTotal().toFixed(2)}?`)) {
      return;
    }
    
    setProcessingCash(true);
    try {
      const response = await axios.post(`${API}/pos/cash-payment`, {
        customer_first_name: customerName.firstName || 'POS',
        customer_last_name: customerName.lastName || 'Customer',
        customer_email: customerEmail || null,
        items: cartItems.map(item => ({
          sku: item.sku,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          type: item.type,
          days: item.days
        })),
        custom_items: customItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        reschedule_fees: rescheduleFeesInCart.map(fee => ({
          id: fee.id,
          name: fee.name,
          quantity: fee.quantity,
          price: fee.price
        })),
        fees: {
          after_hours: manualFees.afterHours,
          rush: manualFees.rushFee,
          mileage: manualFees.mileage
        },
        discount: {
          type: discount.type,
          value: discount.value || 0,
          amount: getDiscountAmount(),
          reason: discount.reason || ''
        },
        total_quantity: getTotalQuantity(),
        total: getTotal(),
        pending_order_id: loadedFromPendingOrder || null
      });
      
      if (response.data.success) {
        setLastOrderId(response.data.woo_order_id);
        toast.success('Cash payment recorded!');
        
        // If loaded from pending order, refresh the list
        if (loadedFromPendingOrder) {
          fetchPendingOrders();
        }
        
        // Show receipt dialog
        setReceiptEmail(customerEmail || '');
        setShowReceiptDialog(true);
      }
    } catch (error) {
      console.error('Cash payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process cash payment');
    } finally {
      setProcessingCash(false);
    }
  };
  
  // Send receipt
  const handleSendReceipt = async () => {
    if (!receiptEmail || !lastOrderId) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingReceipt(true);
    try {
      await axios.post(`${API}/pos/send-receipt`, {
        order_id: lastOrderId,
        email: receiptEmail,
        items: cartItems,
        fees: {
          after_hours: manualFees.afterHours ? feeRates.afterHoursPerUnit * getTotalQuantity() : 0,
          rush: manualFees.rushFee ? feeRates.rushPerUnit * getTotalQuantity() : 0,
          mileage: manualFees.mileage > 0 ? feeRates.mileagePerMile * manualFees.mileage : 0
        },
        discount: {
          type: discount.type,
          value: discount.value || 0,
          amount: getDiscountAmount(),
          reason: discount.reason || ''
        },
        total: getTotal()
      });
      
      toast.success('Receipt sent!');
      handleCloseAndReset();
    } catch (error) {
      toast.error('Failed to send receipt');
    } finally {
      setSendingReceipt(false);
    }
  };
  
  // Close without receipt
  const handleSkipReceipt = () => {
    handleCloseAndReset();
  };
  
  // Reset and close
  const handleCloseAndReset = () => {
    setShowReceiptDialog(false);
    setCartItems([]);
    setManualFees({ afterHours: false, rushFee: false, mileage: 0 });
    setReceiptEmail('');
    setLastOrderId(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open && !showReceiptDialog} onOpenChange={(isOpen) => !isOpen && !isFullscreen && onClose()}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-[98vw] w-[98vw] h-[95vh] max-h-[95vh] flex flex-col overflow-hidden p-0">
          <div ref={posContainerRef} className="flex flex-col h-full w-full bg-slate-900">
          {/* Hidden exit tap zone - top right corner (only visible in fullscreen) */}
          {isFullscreen && (
            <div 
              className="absolute top-0 right-0 w-20 h-20 z-50 cursor-default"
              onClick={handleExitTap}
              style={{ touchAction: 'manipulation' }}
            />
          )}
          
          {/* Exit Confirmation Dialog */}
          <Dialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
            <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
              <DialogHeader>
                <DialogTitle>Exit Kiosk Mode?</DialogTitle>
              </DialogHeader>
              <p className="text-slate-300">Are you sure you want to exit fullscreen kiosk mode?</p>
              <DialogFooter className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowExitConfirm(false)}>Cancel</Button>
                <Button onClick={exitFullscreen} className="bg-red-600 hover:bg-red-500">Exit Kiosk Mode</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-3 bg-slate-800 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <ShoppingCart className="h-7 w-7 text-emerald-400" />
              <h1 className="text-2xl font-bold text-white">Point of Sale</h1>
            </div>
            <div className="flex items-center gap-4">
              {pricesCacheInfo.lastUpdated && (
                <span className="text-xs text-slate-500">
                  Prices updated: {new Date(pricesCacheInfo.lastUpdated).toLocaleString()}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={refreshPricesManually}
                disabled={refreshingPrices}
                className="text-white hover:text-teal-400"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshingPrices ? 'animate-spin' : ''}`} />
                Refresh Prices
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPendingOrders(true)}
                className="border-teal-600/50 text-white hover:bg-blue-900/30 relative"
                data-testid="pos-pending-orders-btn"
              >
                <List className="h-4 w-4 mr-2" />
                Pending Orders
                {pendingOrders.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingOrders.length}
                  </span>
                )}
              </Button>
              {/* Kiosk Mode Button */}
              {!isFullscreen ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={enterFullscreen}
                  className="border-purple-600/50 text-white hover:bg-purple-900/30"
                  data-testid="pos-kiosk-mode-btn"
                >
                  <Maximize className="h-4 w-4 mr-2" />
                  Kiosk Mode
                </Button>
              ) : (
                <span className="text-xs text-purple-400 px-2 py-1 bg-purple-900/30 rounded">
                  Kiosk Mode Active
                </span>
              )}
              {!isFullscreen && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="text-white hover:text-white"
                >
                  <X className="h-5 w-5" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Main Content - 3 Column Layout */}
          <div className="flex-1 flex overflow-hidden">
            {/* LEFT: Product Selection */}
            <div className="w-[35%] border-r border-slate-700 flex flex-col bg-slate-850">
              <div className="p-4 border-b border-slate-700 bg-slate-800">
                <h2 className="font-semibold text-lg text-white">Select Items</h2>
              </div>
              <ScrollArea className="flex-1 p-4">
                {loadingPrices ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Class Products */}
                    <div>
                      <h3 className="text-sm font-medium text-slate-400 mb-2">Classes</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {classTypesWithPrices.map(ct => (
                          <Card 
                            key={ct.id}
                            className="bg-slate-700/50 border-teal-600 p-4 cursor-pointer hover:bg-slate-700 hover:border-teal-500 transition-all active:scale-95"
                            onClick={() => addToCart(ct)}
                            data-testid={`pos-item-${ct.id}`}
                          >
                            <p className="font-medium text-white text-sm truncate">{ct.name}</p>
                            <p className="text-xl font-bold text-emerald-400 mt-1">${ct.wooPrice?.toFixed(2)}</p>
                            <p className="text-xs text-slate-500 mt-1">SKU: {ct.sku}</p>
                          </Card>
                        ))}
                      </div>
                    </div>
                    
                    {/* Rental Products */}
                    {rentalProducts.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-slate-400 mb-2">Rentals</h3>
                        <div className="grid grid-cols-2 gap-3">
                          {rentalProducts.map(rental => (
                            <Card 
                              key={rental.id}
                              className="bg-slate-700/50 border-purple-600 p-4 cursor-pointer hover:bg-slate-700 hover:border-purple-500 transition-all active:scale-95"
                              onClick={() => addToCart(rental)}
                              data-testid={`pos-rental-${rental.sku}`}
                            >
                              <p className="font-medium text-white text-sm truncate">{rental.name}</p>
                              <p className="text-xl font-bold text-purple-400 mt-1">${rental.wooPrice?.toFixed(2)}</p>
                              <p className="text-xs text-slate-500 mt-1">SKU: {rental.sku}</p>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Disposable Supplies */}
                    {disposableProducts.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-slate-400 mb-2">Disposable Supplies</h3>
                        <div className="grid grid-cols-2 gap-3">
                          {disposableProducts.map(item => (
                            <Card 
                              key={item.id}
                              className="bg-slate-700/50 border-amber-600 p-4 cursor-pointer hover:bg-slate-700 hover:border-amber-500 transition-all active:scale-95"
                              onClick={() => addToCart(item)}
                              data-testid={`pos-disposable-${item.sku}`}
                            >
                              <p className="font-medium text-white text-sm truncate">{item.name}</p>
                              <p className="text-xl font-bold text-amber-400 mt-1">${item.wooPrice?.toFixed(2)}</p>
                              <p className="text-xs text-slate-500 mt-1">SKU: {item.sku}</p>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Add-on Products */}
                    {addonProducts.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-slate-400 mb-2">Add-ons</h3>
                        <div className="grid grid-cols-2 gap-3">
                          {addonProducts.map(addon => (
                            <Card 
                              key={addon.id}
                              className="bg-slate-700/50 border-cyan-600 p-4 cursor-pointer hover:bg-slate-700 hover:border-cyan-500 transition-all active:scale-95"
                              onClick={() => addToCart(addon)}
                              data-testid={`pos-addon-${addon.sku}`}
                            >
                              <p className="font-medium text-white text-sm truncate">{addon.name}</p>
                              <p className="text-xl font-bold text-cyan-400 mt-1">${addon.wooPrice?.toFixed(2)}</p>
                              <p className="text-xs text-slate-500 mt-1">SKU: {addon.sku}</p>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Instructor eCards (Instructor Pricing) */}
                    {ecardProducts.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-slate-400 mb-2">eCards (Instructor Pricing)</h3>
                        <div className="grid grid-cols-2 gap-3">
                          {ecardProducts.map(ecard => (
                            <Card 
                              key={ecard.id}
                              className="bg-slate-700/50 border-rose-600 p-4 cursor-pointer hover:bg-slate-700 hover:border-rose-500 transition-all active:scale-95"
                              onClick={() => addToCart(ecard)}
                              data-testid={`pos-ecard-${ecard.product_number}`}
                            >
                              <p className="font-medium text-white text-sm truncate">{ecard.name}</p>
                              <p className="text-xl font-bold text-rose-400 mt-1">${ecard.wooPrice?.toFixed(2)}</p>
                              <p className="text-xs text-slate-500 mt-1">#{ecard.product_number}</p>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
              
              {/* Additional Fees Section */}
              <div className="p-4 border-t border-slate-700 bg-slate-800">
                <h3 className="font-medium text-white mb-3">Additional Fees</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-white">After Hours</Label>
                    <div className="flex items-center mt-1">
                      <Button size="sm" variant="outline" onClick={() => setManualFees(f => ({...f, afterHoursQty: Math.max(0, f.afterHoursQty - 1)}))} className="h-8 w-8 p-0 border-teal-600">
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-10 text-center text-white font-medium">{manualFees.afterHoursQty}</span>
                      <Button size="sm" variant="outline" onClick={() => setManualFees(f => ({...f, afterHoursQty: f.afterHoursQty + 1}))} className="h-8 w-8 p-0 border-teal-600">
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-white">Last Minute</Label>
                    <div className="flex items-center mt-1">
                      <Button size="sm" variant="outline" onClick={() => setManualFees(f => ({...f, rushFeeQty: Math.max(0, f.rushFeeQty - 1)}))} className="h-8 w-8 p-0 border-teal-600">
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-10 text-center text-white font-medium">{manualFees.rushFeeQty}</span>
                      <Button size="sm" variant="outline" onClick={() => setManualFees(f => ({...f, rushFeeQty: f.rushFeeQty + 1}))} className="h-8 w-8 p-0 border-teal-600">
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-white">Miles</Label>
                    <div className="flex items-center gap-1 mt-1">
                      <Input
                        type="number"
                        min="0"
                        value={manualFees.mileage || ''}
                        onChange={(e) => setManualFees(f => ({...f, mileage: e.target.value === '' ? '' : e.target.value}))}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value);
                          if (isNaN(val) || val < 0) setManualFees(f => ({...f, mileage: 0}));
                        }}
                        className="h-8 w-16 bg-slate-700 border-teal-600 text-white text-center text-sm"
                      />
                      <Button size="sm" variant="outline" onClick={() => setShowDistanceCalc(true)} className="h-8 w-8 p-0 border-teal-600" title="Calculate Distance">
                        <Navigation className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* MIDDLE: Cart & Reschedule Fees */}
            <div className="w-[35%] border-r border-slate-700 flex flex-col">
              {/* Customer Info */}
              <div className="p-4 border-b border-slate-700 bg-slate-800">
                {/* Recurring Client Selector */}
                <div className="mb-2">
                  <RecurringClientSelector
                    selectedClient={selectedRecurringClient}
                    onSelect={(client) => {
                      setSelectedRecurringClient(client);
                      setCustomerName({
                        firstName: client.first_name || '',
                        lastName: client.last_name || ''
                      });
                      setCustomerEmail(client.email || '');
                    }}
                    onClear={() => setSelectedRecurringClient(null)}
                    placeholder="Recurring client..."
                    className="[&_input]:bg-slate-700 [&_input]:text-white [&_input]:border-slate-600 [&_input]:h-8 [&_input]:text-sm"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    value={customerName.firstName}
                    onChange={(e) => setCustomerName(n => ({ ...n, firstName: e.target.value }))}
                    className="bg-slate-700 border-teal-600 text-white h-9"
                    placeholder="First name"
                  />
                  <Input
                    value={customerName.lastName}
                    onChange={(e) => setCustomerName(n => ({ ...n, lastName: e.target.value }))}
                    className="bg-slate-700 border-teal-600 text-white h-9"
                    placeholder="Last name"
                  />
                  <Input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="bg-slate-700 border-teal-600 text-white h-9"
                    placeholder="Email (optional)"
                  />
                </div>
              </div>
              
              {/* Cart Items */}
              <div className="p-4 border-b border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-lg text-white">Cart</h2>
                  <span className="text-sm text-white">{getTotalQuantity()} items</span>
                </div>
                <ScrollArea className="h-[180px]">
                  {cartItems.length === 0 && customItems.length === 0 && rescheduleFeesInCart.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-500">
                      <ShoppingCart className="h-10 w-10 mb-2 opacity-50" />
                      <p>No items in cart</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {cartItems.map(item => (
                        <div key={item.id} className={`flex items-center justify-between rounded-lg p-2 ${item.type === 'rental' ? 'bg-purple-900/30 border border-purple-700/50' : 'bg-slate-700/50'}`}>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${item.type === 'rental' ? 'text-purple-200' : 'text-white'}`}>{item.name}</p>
                            <p className={`text-xs ${item.type === 'rental' ? 'text-purple-400' : 'text-white'}`}>
                              ${item.price.toFixed(2)}{item.type === 'rental' ? '/day' : ' each'}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Days selector for rentals */}
                            {item.type === 'rental' && (
                              <div className="flex items-center gap-1 mr-2 bg-purple-800/50 rounded px-1">
                                <span className="text-xs text-purple-300">Days:</span>
                                <input
                                  type="number"
                                  min="1"
                                  value={item.days || 1}
                                  onChange={(e) => updateRentalDays(item.id, e.target.value)}
                                  className="w-10 h-6 text-center text-xs bg-purple-900/50 border border-purple-600 rounded text-purple-200"
                                />
                              </div>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => updateQuantity(item.id, -1)} className={`h-7 w-7 p-0 ${item.type === 'rental' ? 'text-purple-400' : 'text-white'}`}>
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className={`w-6 text-center ${item.type === 'rental' ? 'text-purple-200' : 'text-white'}`}>{item.quantity}</span>
                            <Button size="sm" variant="ghost" onClick={() => updateQuantity(item.id, 1)} className={`h-7 w-7 p-0 ${item.type === 'rental' ? 'text-purple-400' : 'text-white'}`}>
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => removeItem(item.id)} className="h-7 w-7 p-0 text-red-400">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <p className={`w-20 text-right font-medium ${item.type === 'rental' ? 'text-purple-400' : 'text-emerald-400'}`}>
                            ${(item.price * item.quantity * (item.type === 'rental' ? (item.days || 1) : 1)).toFixed(2)}
                          </p>
                        </div>
                      ))}
                      {customItems.map(item => (
                        <div key={item.id} className="flex items-center justify-between bg-amber-900/30 border border-amber-700/50 rounded-lg p-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-amber-200 truncate">{item.name}</p>
                            <p className="text-xs text-amber-400">Custom</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => updateCustomItemQuantity(item.id, -1)} className="h-7 w-7 p-0 text-amber-400">
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 text-center text-amber-200">{item.quantity}</span>
                            <Button size="sm" variant="ghost" onClick={() => updateCustomItemQuantity(item.id, 1)} className="h-7 w-7 p-0 text-amber-400">
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => removeCustomItem(item.id)} className="h-7 w-7 p-0 text-red-400">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <p className="w-16 text-right font-medium text-amber-400">${(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      ))}
                      {rescheduleFeesInCart.map(fee => (
                        <div key={fee.id} className="flex items-center justify-between bg-orange-900/30 border border-orange-700/50 rounded-lg p-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-orange-200 truncate">{fee.name.replace('Reschedule Fee - ', 'Resched: ')}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => updateRescheduleFeeQuantity(fee.id, -1)} className="h-7 w-7 p-0 text-orange-400">
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 text-center text-orange-200">{fee.quantity}</span>
                            <Button size="sm" variant="ghost" onClick={() => updateRescheduleFeeQuantity(fee.id, 1)} className="h-7 w-7 p-0 text-orange-400">
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => removeRescheduleFee(fee.id)} className="h-7 w-7 p-0 text-red-400">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <p className="w-16 text-right font-medium text-orange-400">${(fee.price * fee.quantity).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
              
              {/* Reschedule Fees Quick Add */}
              <div className="p-4 border-b border-slate-700 bg-slate-800/50">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-orange-300 flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Reschedule Fees
                  </h3>
                  <Button size="sm" variant="ghost" onClick={openReschedFeeSettings} className="h-6 px-2 text-xs text-white hover:text-white">
                    <Edit2 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {rescheduleFees.map(fee => (
                    <Button
                      key={fee.id}
                      size="sm"
                      variant="outline"
                      onClick={() => addRescheduleFee(fee)}
                      className="border-orange-600/50 text-orange-400 hover:bg-orange-900/30 text-xs h-9 justify-between px-2"
                    >
                      <span className="truncate">{fee.name.replace('Reschedule Fee - ', '')}</span>
                      <span className="text-orange-300 ml-1">${fee.price}</span>
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Custom Item Add */}
              <div className="p-4 flex-1">
                {showAddCustomItem ? (
                  <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Plus className="h-4 w-4 text-amber-400" />
                      <span className="text-sm font-medium text-amber-300">Add Custom Item</span>
                    </div>
                    <Input
                      placeholder="Item name"
                      value={newCustomItem.name}
                      onChange={(e) => setNewCustomItem({...newCustomItem, name: e.target.value})}
                      className="bg-slate-800 border-teal-600 text-white text-sm mb-2"
                    />
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-white" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Price"
                          value={newCustomItem.price}
                          onChange={(e) => setNewCustomItem({...newCustomItem, price: e.target.value})}
                          className="bg-slate-800 border-teal-600 text-white text-sm pl-7"
                        />
                      </div>
                      <Button size="sm" onClick={addCustomItem} className="bg-amber-600 hover:bg-amber-500">Add</Button>
                      <Button size="sm" variant="outline" onClick={() => { setShowAddCustomItem(false); setNewCustomItem({ name: '', price: '' }); }} className="border-teal-600">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setShowAddCustomItem(true)}
                    className="w-full border-dashed border-amber-600/50 text-amber-400 hover:bg-amber-900/20"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Custom Item/Charge
                  </Button>
                )}
              </div>
            </div>
            
            {/* RIGHT: Payment Section */}
            <div className="w-[30%] flex flex-col bg-slate-800">
              {/* Order Summary */}
              <div className="p-4 border-b border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-lg text-white">Order Summary</h2>
                  {loadedFromPendingOrder && (
                    <span className="text-xs bg-blue-600/30 text-blue-300 px-2 py-1 rounded">
                      Loaded: #{loadedFromPendingOrder}
                    </span>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white">Subtotal</span>
                    <span className="text-white">${getSubtotal().toFixed(2)}</span>
                  </div>
                  {customItems.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-amber-400">Custom Items</span>
                      <span className="text-amber-400">+${getCustomItemsTotal().toFixed(2)}</span>
                    </div>
                  )}
                  {rescheduleFeesInCart.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-orange-400">Reschedule Fees</span>
                      <span className="text-orange-400">+${getRescheduleFeesTotal().toFixed(2)}</span>
                    </div>
                  )}
                  {getFeesTotal() > 0 && (
                    <div className="flex justify-between">
                      <span className="text-teal-400">Additional Fees</span>
                      <span className="text-teal-400">+${getFeesTotal().toFixed(2)}</span>
                    </div>
                  )}
                  {getTravelMilesTotal() > 0 && (
                    <div className="flex justify-between">
                      <span className="text-cyan-400">Travel Miles ({manualFees.mileage} mi)</span>
                      <span className="text-cyan-400">+${getTravelMilesTotal().toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* Discount Section */}
                  <div className="border-t border-teal-600 pt-2 mt-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Percent className="h-4 w-4 text-purple-400" />
                      <span className="text-purple-400 font-medium">Discount</span>
                      <div className="flex bg-slate-700 rounded-md p-0.5 ml-auto">
                        <button
                          type="button"
                          onClick={() => setDiscount({ ...discount, type: 'dollar' })}
                          className={`px-2 py-0.5 text-xs rounded ${discount.type === 'dollar' ? 'bg-purple-600 text-white' : 'text-white'}`}
                        >$</button>
                        <button
                          type="button"
                          onClick={() => setDiscount({ ...discount, type: 'percent' })}
                          className={`px-2 py-0.5 text-xs rounded ${discount.type === 'percent' ? 'bg-purple-600 text-white' : 'text-white'}`}
                        >%</button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="relative w-20">
                        {discount.type === 'dollar' && <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-white" />}
                        <Input
                          type="number"
                          min="0"
                          value={discount.value ?? ''}
                          onChange={(e) => setDiscount({ ...discount, value: e.target.value === '' ? '' : e.target.value })}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (isNaN(val) || val < 0) setDiscount(prev => ({ ...prev, value: 0 }));
                            else if (discount.type === 'percent' && val > 100) setDiscount(prev => ({ ...prev, value: 100 }));
                            else setDiscount(prev => ({ ...prev, value: val }));
                          }}
                          placeholder="0"
                          className={`${discount.type === 'dollar' ? 'pl-6' : 'pl-2'} h-8 bg-slate-700 border-teal-600 text-white text-sm`}
                        />
                        {discount.type === 'percent' && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-white text-xs">%</span>}
                      </div>
                      <Input
                        value={discount.reason}
                        onChange={(e) => setDiscount({ ...discount, reason: e.target.value })}
                        placeholder="Reason"
                        className="flex-1 h-8 bg-slate-700 border-teal-600 text-white text-sm"
                      />
                    </div>
                  </div>
                  
                  {getDiscountAmount() > 0 && (
                    <div className="flex justify-between">
                      <span className="text-purple-400">Discount Applied</span>
                      <span className="text-purple-400">-${getDiscountAmount().toFixed(2)}</span>
                    </div>
                  )}
                </div>
                
                {/* Total */}
                <div className="flex justify-between text-2xl font-bold border-t border-teal-600 pt-3 mt-3">
                  <span className="text-white">Total</span>
                  <span className="text-emerald-400">${getTotal().toFixed(2)}</span>
                </div>
              </div>
              
              {/* Payment Form */}
              <div className="flex-1 p-4 overflow-y-auto">
                <h2 className="font-semibold text-lg text-white mb-3 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-emerald-400" />
                  Payment
                </h2>
                
                {getTotal() <= 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-slate-500">
                    <CreditCard className="h-12 w-12 mb-3 opacity-50" />
                    <p>Add items to process payment</p>
                  </div>
                ) : stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <div className="space-y-4">
                      {/* Card Number */}
                      <div>
                        <Label className="text-white text-sm mb-2 flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-slate-400" />
                          Card Number
                        </Label>
                        <div className={`bg-slate-700 border rounded-lg p-3 ${
                          cardFieldStatus.cardNumber.error 
                            ? 'border-red-500' 
                            : cardFieldStatus.cardNumber.complete 
                              ? 'border-green-500' 
                              : 'border-teal-600'
                        }`}>
                          <CardNumberElement 
                            options={ELEMENT_OPTIONS}
                            onChange={(e) => setCardFieldStatus(prev => ({
                              ...prev,
                              cardNumber: { complete: e.complete, error: e.error?.message || null }
                            }))}
                          />
                        </div>
                        {cardFieldStatus.cardNumber.error && (
                          <p className="text-xs text-red-400 mt-1">{cardFieldStatus.cardNumber.error}</p>
                        )}
                      </div>
                      
                      {/* Expiry and CVC Row */}
                      <div className="grid grid-cols-2 gap-3">
                        {/* Expiry Date */}
                        <div>
                          <Label className="text-white text-sm mb-2 flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            Expiry
                          </Label>
                          <div className={`bg-slate-700 border rounded-lg p-3 ${
                            cardFieldStatus.cardExpiry.error 
                              ? 'border-red-500' 
                              : cardFieldStatus.cardExpiry.complete 
                                ? 'border-green-500' 
                                : 'border-teal-600'
                          }`}>
                            <CardExpiryElement 
                              options={ELEMENT_OPTIONS}
                              onChange={(e) => setCardFieldStatus(prev => ({
                                ...prev,
                                cardExpiry: { complete: e.complete, error: e.error?.message || null }
                              }))}
                            />
                          </div>
                        </div>
                        
                        {/* CVC */}
                        <div>
                          <Label className="text-white text-sm mb-2 flex items-center gap-2">
                            <ShieldCheck className="h-4 w-4 text-slate-400" />
                            CVC
                          </Label>
                          <div className={`bg-slate-700 border rounded-lg p-3 ${
                            cardFieldStatus.cardCvc.error 
                              ? 'border-red-500' 
                              : cardFieldStatus.cardCvc.complete 
                                ? 'border-green-500' 
                                : 'border-teal-600'
                          }`}>
                            <CardCvcElement 
                              options={ELEMENT_OPTIONS}
                              onChange={(e) => setCardFieldStatus(prev => ({
                                ...prev,
                                cardCvc: { complete: e.complete, error: e.error?.message || null }
                              }))}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* ZIP Code */}
                      <div>
                        <Label className="text-white text-sm mb-2">ZIP Code</Label>
                        <Input
                          type="text"
                          placeholder="12345"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10))}
                          className={`bg-slate-700 border text-white placeholder:text-slate-500 ${
                            postalCode.length >= 5 ? 'border-green-500' : 'border-teal-600'
                          }`}
                        />
                      </div>
                      
                      <POSStripeSubmitButton
                        onApprove={onStripeApprove}
                        onError={onStripeError}
                        disabled={paymentProcessing || getTotal() <= 0 || !cardFieldStatus.cardNumber.complete || !cardFieldStatus.cardExpiry.complete || !cardFieldStatus.cardCvc.complete || postalCode.length < 5}
                        total={getTotal()}
                        customerEmail={customerEmail}
                        customerName={`${customerName.firstName} ${customerName.lastName}`.trim()}
                        postalCode={postalCode}
                        metadata={{
                          customer_name: `${customerName.firstName} ${customerName.lastName}`.trim(),
                          recurring_client_id: selectedRecurringClient?.id || ''
                        }}
                      />
                        
                        {/* Cash Payment Button */}
                        <Button
                          onClick={handleCashPayment}
                          disabled={processingCash || paymentProcessing || getTotal() <= 0}
                          className="w-full h-12 bg-green-700 hover:bg-green-600 text-white font-semibold text-lg"
                          data-testid="pos-cash-payment"
                        >
                          {processingCash ? (
                            <>
                              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Banknote className="h-5 w-5 mr-2" />
                              Cash ${getTotal().toFixed(2)}
                            </>
                          )}
                        </Button>
                        
                        {/* Divider */}
                        <div className="flex items-center gap-3 py-2">
                          <div className="flex-1 h-px bg-slate-600"></div>
                          <span className="text-xs text-slate-500">or</span>
                          <div className="flex-1 h-px bg-slate-600"></div>
                        </div>
                        
                        {/* Convert to Quote & Send Button */}
                        <Button
                          onClick={convertToQuoteAndSend}
                          disabled={convertingToQuote || getTotal() <= 0}
                          className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
                          data-testid="pos-convert-to-quote"
                        >
                          {convertingToQuote ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Creating Quote...
                            </>
                          ) : (
                            <>
                              <FileText className="h-4 w-4 mr-2" />
                              Convert to Quote & Send
                            </>
                          )}
                        </Button>
                        
                        {/* Validation hint for quote button */}
                        {getTotal() > 0 && (!customerName.firstName?.trim() && !customerName.lastName?.trim() || !customerEmail?.trim()) && (
                          <p className="text-xs text-amber-400 text-center">
                            ⚠️ Name and email required to send quote
                          </p>
                        )}
                        
                        {/* Save to Pending Orders Button */}
                        <Button
                          onClick={saveToPendingOrders}
                          disabled={savingToPending || getTotal() <= 0}
                          variant="outline"
                          className="w-full border-slate-500 text-white hover:bg-slate-700"
                          data-testid="pos-save-to-pending"
                        >
                          {savingToPending ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Clock className="h-4 w-4 mr-2" />
                              Save to Pending Orders
                            </>
                          )}
                        </Button>
                      </div>
                  </Elements>
                ) : (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
                    <span className="ml-3 text-white">Loading payment...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={() => {}}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-emerald-400" />
              Payment Complete!
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="text-center mb-6">
              <CheckCircle className="h-16 w-16 text-emerald-400 mx-auto mb-3" />
              <p className="text-2xl font-bold text-emerald-400">${getTotal().toFixed(2)}</p>
              <p className="text-white text-sm">Transaction successful</p>
            </div>
            
            <div className="space-y-4">
              <p className="text-center text-white">Would you like to send a receipt?</p>
              
              <div>
                <Label className="text-white">Email Address</Label>
                <Input
                  type="email"
                  value={receiptEmail}
                  onChange={(e) => setReceiptEmail(e.target.value)}
                  placeholder="customer@email.com"
                  className="mt-1 bg-slate-700 border-teal-600 text-white"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="ghost"
              onClick={handleSkipReceipt}
              className="flex-1 text-white hover:text-white"
            >
              No Receipt
            </Button>
            <Button
              onClick={handleSendReceipt}
              disabled={!receiptEmail || sendingReceipt}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500"
            >
              {sendingReceipt ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Receipt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Distance Calculator */}
      <DistanceCalculator
        open={showDistanceCalc}
        onClose={() => setShowDistanceCalc(false)}
        onImport={(miles) => setManualFees(f => ({ ...f, mileage: miles }))}
      />
      
      {/* Reschedule Fee Settings Dialog */}
      <Dialog open={showReschedFeeSettings} onOpenChange={setShowReschedFeeSettings}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-orange-400" />
              Edit Reschedule Fee Prices
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {editingFees.map(fee => (
              <div key={fee.id} className="flex items-center justify-between gap-3">
                <span className="text-sm text-white flex-1 truncate">
                  {fee.name.replace('Reschedule Fee - ', '')}
                </span>
                <div className="relative w-24">
                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-white" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={fee.price}
                    onChange={(e) => updateEditingFeePrice(fee.id, e.target.value)}
                    className="bg-slate-700 border-teal-600 text-white pl-7 h-9"
                    data-testid={`pos-edit-fee-${fee.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowReschedFeeSettings(false)}
              className="border-teal-600 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={saveRescheduleFees}
              disabled={savingFees}
              className="bg-orange-600 hover:bg-orange-500 text-white"
              data-testid="pos-save-resched-fees"
            >
              {savingFees ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Pending Orders Dialog */}
      <Dialog open={showPendingOrders} onOpenChange={setShowPendingOrders}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-5 w-5 text-white" />
              Pending Payment Orders
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="flex-1 -mx-6 px-6">
            {pendingOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <Clock className="h-12 w-12 mb-3 opacity-50" />
                <p>No pending orders</p>
              </div>
            ) : (
              <div className="space-y-3 py-4">
                {pendingOrders.map(order => {
                  const hasCustomerName = order.customer_name?.firstName || order.customer_name?.lastName;
                  const customerFullName = hasCustomerName 
                    ? `${order.customer_name?.firstName || ''} ${order.customer_name?.lastName || ''}`.trim()
                    : null;
                  
                  return (
                  <div key={order.order_id} className="bg-slate-700/50 border border-slate-600 rounded-lg p-4 hover:border-teal-500 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm text-white">#{order.order_id}</span>
                          <span className="text-emerald-400 font-bold">${order.total?.toFixed(2)}</span>
                        </div>
                        {/* Customer Info */}
                        <div className="space-y-0.5">
                          {customerFullName ? (
                            <p className="text-sm text-white font-medium">{customerFullName}</p>
                          ) : (
                            <p className="text-sm text-slate-500 italic">No customer name</p>
                          )}
                          {order.customer_email ? (
                            <p className="text-xs text-teal-400">{order.customer_email}</p>
                          ) : (
                            <p className="text-xs text-slate-500 italic">No email</p>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(order.created_at).toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-400 mb-3">
                      {order.items?.map(i => `${i.name} x${i.quantity}`).join(', ') || 'No items'}
                      {order.custom_items?.length > 0 && ` + ${order.custom_items.length} custom`}
                      {order.reschedule_fees?.length > 0 && ` + ${order.reschedule_fees.length} resched fees`}
                    </div>
                    
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Primary action - Recall to POS */}
                      <Button
                        size="sm"
                        onClick={() => loadPendingOrder(order)}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white flex-1"
                        data-testid={`recall-order-${order.order_id}`}
                      >
                        <ShoppingCart className="h-3 w-3 mr-1" />
                        Recall to POS
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const url = `${window.location.origin}/pos-payment/${order.order_id}`;
                          navigator.clipboard.writeText(url);
                          toast.success('Payment link copied!');
                        }}
                        className="border-teal-600 text-white"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy Link
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => deletePendingOrder(e, order.order_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPendingOrders(false)}
              className="border-teal-600 text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default POSSystem;
